import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import { getYear } from "../utils/affiliateHelper";
import ArtistLinks from "./ArtistLinks";
import VoteButton from "./VoteButton";
import { useTopHype } from "../context/TopHypeContext";

export function CoverImage({ cover_url, interpret, titel, className }) {
  const [imgError, setImgError] = useState(false);
  const initials = `${interpret?.[0] || ""}${titel?.[0] || ""}`.toUpperCase();

  if (!cover_url || imgError) {
    return (
      <div className={`cover-fallback ${className}`}>
        <span className="text-2xl md:text-3xl">{initials}</span>
      </div>
    );
  }
  return (
    <img
      src={cover_url}
      alt={`${interpret} – ${titel}`}
      className={`object-cover w-full h-full ${className}`}
      onError={() => setImgError(true)}
      loading="lazy"
    />
  );
}

export default function ReleaseCard({ release, onClick, rank }) {
  const topHypeIds = useTopHype();
  const isTopHyped = release?.id && topHypeIds.has(release.id);
  const year = getYear(release.erscheinungsdatum);

  return (
    <div
      data-testid="release-card"
      onClick={() => onClick && onClick(release)}
      className="group cursor-pointer"
    >
      {/* Cover */}
      <div className="aspect-square overflow-hidden rounded-2xl bg-zinc-900 relative mb-3 shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
        <div className="w-full h-full transition-transform duration-500 group-hover:scale-105">
          <CoverImage
            cover_url={release.cover_url}
            interpret={release.interpret}
            titel={release.titel}
            className="w-full h-full"
          />
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-3">
          <span className="text-white/90 text-xs font-semibold bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-lg">
            Details
          </span>
          {release.id && (
            <div onClick={e => e.stopPropagation()}>
              <VoteButton release={release} size={13} showCount={false} />
            </div>
          )}
        </div>

        {/* Type badge – oben links */}
        {release.typ && (
          <div className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-md text-white/90 text-[10px] px-2 py-0.5 rounded-lg font-semibold tracking-wide">
            {release.typ}
          </div>
        )}

        {/* Platzierungs-Badge – unten links, innerhalb des Covers */}
        {rank != null && (
          <div
            className="absolute bottom-2 left-2 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shadow-lg z-10"
            style={
              rank === 1 ? { background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "#fff", boxShadow: "0 2px 8px rgba(245,158,11,0.5)" } :
              rank === 2 ? { background: "linear-gradient(135deg,#9ca3af,#6b7280)", color: "#fff", boxShadow: "0 2px 8px rgba(156,163,175,0.4)" } :
              rank === 3 ? { background: "linear-gradient(135deg,#b45309,#92400e)", color: "#fff", boxShadow: "0 2px 8px rgba(180,83,9,0.4)" } :
              { background: "rgba(0,0,0,0.65)", color: "#a1a1aa", border: "1px solid rgba(255,255,255,0.1)" }
            }
          >
            {rank}
          </div>
        )}

        {/* Top-Hype-Badge dieses Monats */}
        {isTopHyped && (
          <div
            title="Einer der meistgehypten Releases diesen Monat"
            className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-amber-500/90 backdrop-blur-md text-black text-[10px] font-black px-2 py-0.5 rounded-lg shadow-lg"
          >
            <Flame size={9} className="fill-black" />
            HYPE
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="space-y-0.5 px-0.5">
        <div className="truncate leading-snug">
          <ArtistLinks release={release} stopPropagation className="text-white font-semibold text-sm" />
        </div>
        <p className="text-zinc-500 text-xs truncate">{release.titel}</p>
        {year && <p className="text-zinc-700 text-xs">{year}</p>}
      </div>
    </div>
  );
}
